import * as RadixToast from '@radix-ui/react-toast';
import { create } from 'zustand';
import { cn } from './cn';
import { Icon } from './Icon';

interface ToastItem {
  id: number;
  title: string;
  description?: string;
  tone: 'success' | 'error' | 'info';
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface ToastState {
  items: ToastItem[];
  push(t: Omit<ToastItem, 'id'>): void;
  remove(id: number): void;
}

let seq = 0;
export const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (t) => set((s) => ({ items: [...s.items, { ...t, id: ++seq }] })),
  remove: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
}));

export const toast = {
  success: (title: string, description?: string) => useToasts.getState().push({ title, description, tone: 'success' }),
  error: (title: string, description?: string) => useToasts.getState().push({ title, description, tone: 'error' }),
  info: (title: string, description?: string, action?: ToastItem['action'], durationMs?: number) => useToasts.getState().push({ title, description, tone: 'info', action, durationMs }),
};

const toneClass = { success: 'border-brand-600', error: 'border-red-600', info: 'border-gold-500' };

export function Toaster() {
  const { items, remove } = useToasts();
  return (
    <RadixToast.Provider swipeDirection="down" duration={5000}>
      {items.map((t) => (
        <RadixToast.Root
          key={t.id}
          duration={t.durationMs}
          onOpenChange={(open) => !open && remove(t.id)}
          className={cn('flex items-start gap-3 rounded-2xl border-l-4 bg-white p-4 shadow-xl ring-1 ring-black/5 data-[state=open]:animate-slide-up', toneClass[t.tone])}
        >
          <div className="flex-1">
            <RadixToast.Title className="font-semibold text-gray-900">{t.title}</RadixToast.Title>
            {t.description && <RadixToast.Description className="mt-0.5 text-sm text-gray-600">{t.description}</RadixToast.Description>}
          </div>
          {t.action && (
            <RadixToast.Action altText={t.action.label} asChild>
              <button className="min-h-[40px] rounded-lg bg-brand-800 px-3 text-sm font-semibold text-white" onClick={t.action.onClick}>
                {t.action.label}
              </button>
            </RadixToast.Action>
          )}
          <RadixToast.Close aria-label="Fermer" className="text-gray-400 hover:text-gray-600">
            <Icon name="close" size={18} />
          </RadixToast.Close>
        </RadixToast.Root>
      ))}
      <RadixToast.Viewport className="fixed inset-x-0 bottom-[calc(76px+env(safe-area-inset-bottom))] z-[60] mx-auto flex max-w-lg flex-col gap-2 px-4 outline-none" />
    </RadixToast.Provider>
  );
}
